// src/components/tutorials/JS_BigInt.jsx
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_BigInt() {
  const refs = {
    INTRO: useRef(null),
    CREATION: useRef(null),
    OPERATIONS: useRef(null),
    COMPARISON: useRef(null),
    TYPE_CHECK: useRef(null),
    LIMITATIONS: useRef(null),
    CONVERSION: useRef(null),
  };

  const scrollTo = (key) =>
    refs[key]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">💥 JavaScript BigInt</h1>
      <p className="tutorial-intro">
        The <b>BigInt</b> type in JavaScript allows representation of integers
        larger than{" "}
        <code className="highlight_code">Number.MAX_SAFE_INTEGER</code>. It’s
        useful for calculations that need very large or precise integer values.
      </p>

      {/* Two-column Key Section */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "2rem",
          background: "linear-gradient(135deg, #e9d5ff, #c084fc)",
          padding: "1.5rem",
          borderRadius: "12px",
          marginTop: "1rem",
        }}
      >
        {/* Column 1 */}
        <div>
          <h2 style={{ margin: 0, color: "#5b21b6" }}>BigInt Topics</h2>
          <ul
            style={{ listStyleType: "none", padding: 0, marginTop: "0.5rem" }}
          >
            {[
              { key: "INTRO", label: "What is BigInt?" },
              { key: "CREATION", label: "Creating BigInt" },
              { key: "OPERATIONS", label: "Arithmetic Operations" },
              { key: "COMPARISON", label: "Comparisons with BigInt" },
            ].map((item) => (
              <li key={item.key}>
                <button
                  onClick={() => scrollTo(item.key)}
                  style={{
                    background: "transparent",
                    border: "none",
                    textDecoration: "underline",
                    color: "black",
                    cursor: "pointer",
                    fontSize: "1.05rem",
                    marginBottom: "0.3rem",
                  }}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 2 */}
        <div>
          <h2 style={{ margin: 0, color: "#5b21b6" }}>&nbsp;</h2>
          <ul
            style={{ listStyleType: "none", padding: 0, marginTop: "0.5rem" }}
          >
            {[
              { key: "TYPE_CHECK", label: "Type Checking" },
              { key: "LIMITATIONS", label: "BigInt Limitations" },
              {
                key: "CONVERSION",
                label: "Conversion Between BigInt & Number",
              },
            ].map((item) => (
              <li key={item.key}>
                <button
                  onClick={() => scrollTo(item.key)}
                  style={{
                    background: "transparent",
                    border: "none",
                    textDecoration: "underline",
                    color: "black",
                    cursor: "pointer",
                    fontSize: "1.05rem",
                    marginBottom: "0.3rem",
                  }}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Sections */}

      {/* INTRO */}
      <div ref={refs.INTRO} className="tutorial-section">
        <h3>1️⃣ What is BigInt?</h3>
        <p>
          <code className="highlight_code">BigInt</code> is a special numeric
          type that can represent integers beyond the safe integer range of{" "}
          <code className="highlight_code">Number</code>.
        </p>
        <CodeCopyBlock
          code={`console.log(Number.MAX_SAFE_INTEGER); // 9007199254740991
console.log(9007199254740992n); // valid BigInt`}
        />
        <TryItButton
          code={`console.log(Number.MAX_SAFE_INTEGER);
console.log(9007199254740992n);`}
        />
        <OutputBox>
          Output:
          <br />
          9007199254740991
          <br />
          9007199254740992n
        </OutputBox>
      </div>

      {/* CREATION */}
      <div ref={refs.CREATION} className="tutorial-section">
        <h3>2️⃣ Creating BigInt</h3>
        <p>You can create a BigInt using:</p>
        <ul>
          <li>
            Adding <code className="highlight_code">n</code> at the end of an
            integer literal.
          </li>
          <li>
            Using the <code className="highlight_code">BigInt()</code>{" "}
            constructor.
          </li>
        </ul>
        <CodeCopyBlock
          code={`let big1 = 123456789012345678901234567890n;
let big2 = BigInt("123456789012345678901234567890");
console.log(big1);
console.log(big2);`}
        />
        <TryItButton
          code={`let big1 = 123456789012345678901234567890n;
let big2 = BigInt("123456789012345678901234567890");
console.log(big1);
console.log(big2);`}
        />
        <OutputBox>Output: 123456789012345678901234567890n</OutputBox>
      </div>

      {/* OPERATIONS */}
      <div ref={refs.OPERATIONS} className="tutorial-section">
        <h3>3️⃣ Arithmetic Operations</h3>
        <p>
          You can perform arithmetic with BigInt values just like normal
          numbers.
        </p>
        <CodeCopyBlock
          code={`let a = 10n;
let b = 3n;
console.log(a + b); // 13n
console.log(a * b); // 30n
console.log(a ** b); // 1000n`}
        />
        <TryItButton
          code={`let a = 10n;
let b = 3n;
console.log(a + b);
console.log(a * b);
console.log(a ** b);`}
        />
        <OutputBox>
          Output:
          <br />
          13n
          <br />
          30n
          <br />
          1000n
        </OutputBox>
      </div>

      {/* COMPARISON */}
      <div ref={refs.COMPARISON} className="tutorial-section">
        <h3>4️⃣ Comparisons with BigInt</h3>
        <p>BigInt values can be compared with regular numbers.</p>
        <CodeCopyBlock
          code={`console.log(10n > 5); // true
console.log(10n === 10); // false (different types)
console.log(10n == 10); // true (loose equality)`}
        />
        <TryItButton
          code={`console.log(10n > 5);
console.log(10n === 10);
console.log(10n == 10);`}
        />
        <OutputBox>
          Output:
          <br />
          true
          <br />
          false
          <br />
          true
        </OutputBox>
      </div>

      {/* TYPE_CHECK */}
      <div ref={refs.TYPE_CHECK} className="tutorial-section">
        <h3>5️⃣ Type Checking</h3>
        <p>
          Use <code className="highlight_code">typeof</code> to check if a
          variable is a BigInt.
        </p>
        <CodeCopyBlock
          code={`let x = 123n;
console.log(typeof x); // "bigint"`}
        />
        <TryItButton code={`let x = 123n; console.log(typeof x);`} />
        <OutputBox>Output: "bigint"</OutputBox>
      </div>

      {/* LIMITATIONS */}
      <div ref={refs.LIMITATIONS} className="tutorial-section">
        <h3>6️⃣ BigInt Limitations</h3>
        <ul>
          <li>
            Cannot mix <code className="highlight_code">BigInt</code> and{" "}
            <code className="highlight_code">Number</code> directly in
            arithmetic.
          </li>
          <li>
            No support in <code className="highlight_code">Math</code> methods
            (like <code className="highlight_code">Math.sqrt()</code>).
          </li>
        </ul>
        <CodeCopyBlock
          code={`// console.log(5n + 2); // ❌ TypeError
console.log(5n + 2n); // ✅ 7n`}
        />
        <TryItButton code={`console.log(5n + 2n);`} />
        <OutputBox>Output: 7n</OutputBox>
      </div>

      {/* CONVERSION */}
      <div ref={refs.CONVERSION} className="tutorial-section">
        <h3>7️⃣ Conversion Between BigInt and Number</h3>
        <p>You can convert between BigInt and Number manually.</p>
        <CodeCopyBlock
          code={`let big = 100n;
let num = Number(big);
console.log(num); // 100

let backToBig = BigInt(num);
console.log(backToBig); // 100n`}
        />
        <TryItButton
          code={`let big = 100n;
let num = Number(big);
console.log(num);
let backToBig = BigInt(num);
console.log(backToBig);`}
        />
        <OutputBox>
          Output:
          <br />
          100
          <br />
          100n
        </OutputBox>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #e9d5ff, #c084fc)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#5b21b6" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>Use BigInt for precise integer math beyond 2⁵³.</li>
          <li>Learn how BigInt interacts with JSON and typed arrays.</li>
          <li>Combine BigInt with crypto or large dataset calculations.</li>
        </ul>
      </div>
    </div>
  );
}
